import { useCallback, useEffect, useRef } from 'react';
import type { SparkActivityStageForm } from '../../types/spark-activity';
import type { DomainSkill } from '../../types/domain-skill';
import PointsList from './PointsList';
import SkillsCombobox from './SkillsCombobox';
import PineSelect from './PineSelect';

const STAGE_CONFIG = [
  { name: 'Gestures', bg: '#DCEFE7', color: '#28564A', hint: 'Babbles, points or uses gestures' },
  { name: 'Single words', bg: '#B7DFD0', color: '#1F4C40', hint: 'Uses single words consistently' },
  { name: 'Two words', bg: '#84C6B1', color: '#123B31', hint: 'Combines two or more words' },
  { name: 'Short sentences', bg: '#4B9F87', color: '#ffffff', hint: 'Uses short sentences' },
  { name: 'Conversations', bg: '#20544A', color: '#ffffff', hint: 'Engages in back-and-forth conversation' },
];

interface StageColumnProps {
  stage: SparkActivityStageForm;
  levelIndex: number;
  isFocused?: boolean;
  onFocus?: () => void;
  onChange: (field: keyof SparkActivityStageForm, value: unknown) => void;
  errors: Record<string, string>;
  domains: Array<{ id: number; title: string }>;
  skills: DomainSkill[];
}

export default function StageColumn({
  stage,
  levelIndex,
  isFocused = false,
  onFocus,
  onChange,
  errors,
  domains,
  skills,
}: StageColumnProps) {
  const config = STAGE_CONFIG[levelIndex];
  const descError = errors[`stages.${levelIndex}.description`];
  const pointsError = errors[`stages.${levelIndex}.points`];
  const domainError = errors[`stages.${levelIndex}.domainId`];
  const skillsError = errors[`stages.${levelIndex}.skillIds`];
  const textSize = isFocused ? 'text-[14px]' : 'text-[12px]';

  const descRef = useRef<HTMLTextAreaElement>(null);
  const whyRef = useRef<HTMLTextAreaElement>(null);
  const tipsRef = useRef<HTMLTextAreaElement>(null);

  const autoResize = useCallback((el: HTMLTextAreaElement | null) => {
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, []);

  useEffect(() => {
    autoResize(descRef.current);
    autoResize(whyRef.current);
    autoResize(tipsRef.current);
  }, [isFocused, autoResize]);

  return (
    <div
      data-testid={`stage-column-${levelIndex + 1}`}
      onClick={onFocus}
      className={`p-[14px_12px_16px] border-r-[1.5px] border-pine-line last:border-r-0 transition-all duration-200 ${
        isFocused ? 'bg-white' : 'bg-[#F8FBFA]'
      }`}
    >
      {/* Stage chip */}
      <div
        className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[12px] font-semibold"
        style={{ backgroundColor: config.bg, color: config.color }}
      >
        <span
          className="w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-bold"
          style={{ backgroundColor: config.color, color: config.bg }}
        >
          {levelIndex + 1}
        </span>
        {config.name}
      </div>

      {/* Stage hint */}
      <p className="text-[11px] text-pine-ink-soft min-h-[28px] mt-1.5 mb-2">{config.hint}</p>

      {/* Active toggle */}
      <div className="flex items-center justify-between mb-2">
        <label className="flex items-center gap-1.5 text-xs cursor-pointer select-none">
          <input
            data-testid="stage-active-toggle"
            type="checkbox"
            checked={stage.active}
            onChange={(e) => onChange('active', e.target.checked)}
            className="w-3.5 h-3.5 rounded"
          />
          <span className="text-pine-ink-soft">{stage.active ? 'Active' : 'Inactive'}</span>
        </label>
      </div>

      {/* Stage content */}
      <div>
        {/* Description */}
        <label className="block text-[11px] font-bold uppercase tracking-[0.08em] text-pine-ink-soft mt-[9px] mb-1">
          Description <span className="text-pine-danger">*</span>
        </label>
        <textarea
          ref={descRef}
          value={stage.description}
          onChange={(e) => { autoResize(e.target); onChange('description', e.target.value); }}
          placeholder="Describe what parents should do..."
          className={`w-full border-[1.5px] rounded-[9px] p-[9px_10px] ${textSize} leading-[1.5] bg-white resize-none focus:outline-none transition-colors ${
            descError ? 'border-pine-danger' : 'border-pine-line hover:border-pine-pine focus:border-pine-pine'
          }`}
          style={{ minHeight: '80px' }}
        />
        {descError && <p className="text-[11px] text-pine-danger mt-0.5">{descError}</p>}

        {/* Points */}
        <label className="block text-[11px] font-bold uppercase tracking-[0.08em] text-pine-ink-soft mt-[9px] mb-1">
          How to do it <span className="text-pine-danger">*</span>
        </label>
        <div style={{ minHeight: isFocused ? '240px' : '200px' }}>
          <PointsList
            points={stage.points}
            onChange={(pts) => onChange('points', pts)}
            error={pointsError}
          />
        </div>

        {/* Why It Works */}
        <label className="block text-[11px] font-bold uppercase tracking-[0.08em] text-pine-ink-soft mt-[9px] mb-1">
          Why this works
        </label>
        <textarea
          ref={whyRef}
          value={stage.whyItWorks}
          onChange={(e) => { autoResize(e.target); onChange('whyItWorks', e.target.value); }}
          placeholder="Explain the developmental benefit..."
          className={`w-full border-[1.5px] border-pine-line hover:border-pine-pine rounded-[9px] p-[9px_10px] ${textSize} leading-[1.5] bg-white resize-none focus:border-pine-pine focus:outline-none transition-colors`}
          style={{ minHeight: '80px' }}
        />

                {/* Domain */}
        <label className="block text-[11px] font-bold uppercase tracking-[0.08em] text-pine-ink-soft mt-[9px] mb-1">
          Domain <span className="text-pine-danger">*</span>
        </label>
        <div data-testid="domain-select">
          <PineSelect
            options={domains.map((d) => ({ value: d.id, label: d.title }))}
            value={stage.domainId || undefined}
            onChange={(v) => onChange('domainId', Number(v))}
            placeholder="Select domain..."
            error={!!domainError}
          />
        </div>
        {domainError && <p className="text-[11px] text-pine-danger mt-0.5">{domainError}</p>}

        {/* Skills */}
        <div className="mt-[9px]">
          <SkillsCombobox
            skills={skills}
            selectedIds={stage.skillIds}
            onChange={(ids) => onChange('skillIds', ids)}
            error={skillsError}
          />
        </div>

        {/* Expert Tips */}
        <label className="block text-[11px] font-bold uppercase tracking-[0.08em] text-pine-ink-soft mt-[9px] mb-1">
          Expert tip
        </label>
        <textarea
          ref={tipsRef}
          value={stage.expertTips}
          onChange={(e) => { autoResize(e.target); onChange('expertTips', e.target.value); }}
          placeholder="Share an expert insight..."
          className={`w-full border-[1.5px] border-pine-line hover:border-pine-pine rounded-[9px] p-[9px_10px] ${textSize} leading-[1.5] bg-white resize-none focus:border-pine-pine focus:outline-none transition-colors`}
          style={{ minHeight: '80px' }}
        />

      </div>
    </div>
  );
}
